export interface WebSocketOptions {
  protocols?: string | string[]
  reconnect?: boolean
  reconnectInterval?: number
  maxReconnectAttempts?: number
  heartbeatInterval?: number
  heartbeatMessage?: string
}

export interface WebSocketMessage {
  type: string
  data: any
  timestamp?: number
}

export type WebSocketCallback = (data: any) => void

const DEFAULT_RECONNECT_INTERVAL = 3000
const DEFAULT_MAX_RECONNECT_ATTEMPTS = 5
const DEFAULT_HEARTBEAT_INTERVAL = 30000
const DEFAULT_HEARTBEAT_MESSAGE = 'ping'

export class WebSocketClient {
  private url: string
  private options: WebSocketOptions
  private ws: WebSocket | null = null
  private reconnectAttempts = 0
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null
  private isConnected = false
  private isManualClose = false

  private onOpenCallbacks: WebSocketCallback[] = []
  private onCloseCallbacks: WebSocketCallback[] = []
  private onErrorCallbacks: WebSocketCallback[] = []
  private onMessageCallbacks: WebSocketCallback[] = []
  private messageHandlers: Map<string, WebSocketCallback[]> = new Map()

  constructor(url: string, options: WebSocketOptions = {}) {
    this.url = url
    this.options = {
      reconnect: true,
      reconnectInterval: DEFAULT_RECONNECT_INTERVAL,
      maxReconnectAttempts: DEFAULT_MAX_RECONNECT_ATTEMPTS,
      heartbeatInterval: DEFAULT_HEARTBEAT_INTERVAL,
      heartbeatMessage: DEFAULT_HEARTBEAT_MESSAGE,
      ...options,
    }
    this.connect()
  }

  private connect(): void {
    try {
      this.ws = new WebSocket(this.url, this.options.protocols)
      this.bindEvents()
    } catch (error) {
      this.handleError(error)
    }
  }

  private bindEvents(): void {
    if (!this.ws) return

    this.ws.onopen = () => this.handleOpen()
    this.ws.onclose = (event) => this.handleClose(event)
    this.ws.onerror = (error) => this.handleError(error)
    this.ws.onmessage = (event) => this.handleMessage(event)
  }

  private handleOpen(): void {
    this.isConnected = true
    this.reconnectAttempts = 0
    this.startHeartbeat()
    this.onOpenCallbacks.forEach((cb) => cb(null))
  }

  private handleClose(event: CloseEvent): void {
    this.isConnected = false
    this.stopHeartbeat()
    this.onCloseCallbacks.forEach((cb) => cb(event))

    if (!this.isManualClose && this.options.reconnect) {
      this.reconnect()
    }
  }

  private handleError(error: any): void {
    this.onErrorCallbacks.forEach((cb) => cb(error))
  }

  private handleMessage(event: MessageEvent): void {
    try {
      const message = JSON.parse(event.data) as WebSocketMessage
      this.onMessageCallbacks.forEach((cb) => cb(message))

      const handlers = this.messageHandlers.get(message.type)
      if (handlers) {
        handlers.forEach((cb) => cb(message.data))
      }
    } catch {
      this.onMessageCallbacks.forEach((cb) => cb(event.data))
    }
  }

  private startHeartbeat(): void {
    if (!this.options.heartbeatInterval) return

    this.heartbeatTimer = setInterval(() => {
      this.send(this.options.heartbeatMessage!)
    }, this.options.heartbeatInterval)
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
  }

  private reconnect(): void {
    if (this.options.maxReconnectAttempts && this.reconnectAttempts >= this.options.maxReconnectAttempts) {
      return
    }

    this.reconnectAttempts++
    setTimeout(() => {
      this.connect()
    }, this.options.reconnectInterval)
  }

  public send(data: any): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket is not connected')
    }

    const message = typeof data === 'string' ? data : JSON.stringify(data)
    this.ws.send(message)
  }

  public sendMessage(type: string, data: any): void {
    this.send({ type, data, timestamp: Date.now() })
  }

  public onOpen(callback: WebSocketCallback): void {
    this.onOpenCallbacks.push(callback)
  }

  public onClose(callback: WebSocketCallback): void {
    this.onCloseCallbacks.push(callback)
  }

  public onError(callback: WebSocketCallback): void {
    this.onErrorCallbacks.push(callback)
  }

  public onMessage(callback: WebSocketCallback): void {
    this.onMessageCallbacks.push(callback)
  }

  public on(type: string, callback: WebSocketCallback): void {
    if (!this.messageHandlers.has(type)) {
      this.messageHandlers.set(type, [])
    }
    this.messageHandlers.get(type)!.push(callback)
  }

  public off(type: string, callback?: WebSocketCallback): void {
    if (!callback) {
      this.messageHandlers.delete(type)
      return
    }

    const handlers = this.messageHandlers.get(type)
    if (handlers) {
      const index = handlers.indexOf(callback)
      if (index > -1) {
        handlers.splice(index, 1)
      }
    }
  }

  public close(): void {
    this.isManualClose = true
    this.stopHeartbeat()
    if (this.ws) {
      this.ws.close()
    }
  }

  public getReadyState(): number {
    return this.ws?.readyState ?? WebSocket.CLOSED
  }

  public isOpen(): boolean {
    return this.isConnected
  }
}

export function createWebSocket(url: string, options?: WebSocketOptions): WebSocketClient {
  return new WebSocketClient(url, options)
}
